
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useGroup } from "../../../../queries/groups/UseGroup";
import { useEffect, useMemo } from "react";
import NoData from "../../../no-data/NoData";
import { useTranslation } from "react-i18next";
import { DashboardContainer, DashboardContainerType } from "../../../dashboard/dashboard-container/DashboardContainer";
import NotificationsDetailChartUtils, { IChartCountModel, chartColors, notificationDetailActionItems } from "./NotificationsDetailChartUtils";
import { useAlarmSetpointNotificationCounts } from "../../../../queries/alarm-data/UseAlarmSetpointNotificationCounts";
import { AlarmNotificationsType } from "../../../../services/alarm-data-service/Models";
import { CustomDurationDialogValues } from "../../../custom-duration-dialog/CustomDurationDialog";
import {  List, Stack  } from "@mui/material";
import { AlarmNotificationChip } from "../alarm-notification-chip/AlarmNotificationChip";
import { useCustomerAttribute } from "../../../../queries/attributes/UseCustomerAttribute";
import { isMobile, isTablet } from "react-device-detect";
import { IeLynxObject } from "../../../../common/models/eLynxObject";
import AlarmSetpointEditorDialog, { bindAlarmSetpointEditorDialog, useAlarmSetpointEditorDialogState } from "../../../alarm-setpoint-editor/AlarmSetpointEditorDialog";
import UserEditorDialog from "../../../../views/user-role-administration-view/user-administration/user-editor/UserEditorDialog";
import { bindUserEditorDialog } from "../../../../views/user-role-administration-view/user-administration/user-editor/bindUserEditorDialog";
import { useUserEditorDialogState } from "../../../../views/user-role-administration-view/user-administration/user-editor/useUserEditorDialogState";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { getDeviceRoute, getTankBatteryRoute, getTankRoute, getWellRoute } from "../../../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../../../common/enums/UIViewEnum";
import useNotificationsDetailChartState from "./NotificationsDetailChartState";
import { NotificationsDetailListItem } from "./NotificationsDetailListItem";
import useNavigateTo  from "../../../../hooks/use-navigate-to/useNavigateTo";


export type NotificationsDetailChartProps = {
    notificationsType: AlarmNotificationsType,
    groupID: number,
    durationOptions: CustomDurationDialogValues,
    showEmail: boolean,
    showVoice: boolean,
    showText: boolean,
    targetObject?: IeLynxObject,
    onTargetSelected?: (item: IeLynxObject | undefined) => void

};

export function NotificationsDetailChart({
    notificationsType,
    groupID,
    durationOptions,
    showEmail,
    showVoice,
    showText,
    targetObject,
    onTargetSelected
}: NotificationsDetailChartProps) {

    const isPhone = isMobile && !isTablet;
    const navigateTo = useNavigateTo();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const setpointEditorDialog = useAlarmSetpointEditorDialogState();
    const userEditorDialog = useUserEditorDialogState();

    const {
        state,
        setChartData,
        //setSelectedIndex,
        setSortExpression,
        setSortAscending,
        setTarget,
        resetSortState
    } = useNotificationsDetailChartState();

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data, status, refetch } = useQueryNotifications(useAlarmSetpointNotificationCounts)({
        type: notificationsType,
        organizationID: group?.organizationID ?? -1,
        contextGroupID: groupID,
        startDate: durationOptions.duration.startDate as Date,
        endDate: durationOptions.duration.endDate as Date,
    });

    const { data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID: groupID,
        name: "allow voice alarm",
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute
        ? allowVoiceAlarmAttribute.attributeValue.toLowerCase() === "true"
        : false;

    const title = useMemo(() => {

        return t(notificationsType === AlarmNotificationsType.Setpoints ? 
                    'alarmDashboard.alarmSetpoints' :
                    notificationsType === AlarmNotificationsType.Objects ?
                    'alarmDashboard.alarmObjects' :
                    'alarmDashboard.alarmRecipients');
    }, [
        notificationsType,
        t
    ]);

    const selectedIndex = state.chartData?.findIndex(o => o.elynxObject.id === state.target?.id && o.elynxObject.objectType === state.target?.objectType);

    async function onEditSetpoint(setpointID: number) {

        const result = await setpointEditorDialog.open({
            groupID,
            setpointID: setpointID,
            organizationID: group?.organizationID as number,
            copy: false
        });

        if (result.reason === 'save') {
            setTimeout(() => {
                refetch();
            }, 2000);
        }
    }

    async function onEditUser(userID: number) {

        const result = await userEditorDialog.open({
            userID: userID,
            organizationID: group?.organizationID as number,
        });

        if (result.reason === 'saveChanges') {
            setTimeout(() => refetch(), 2000);
        }
    }

    function navigateToObject(item: IeLynxObject) {

        let url: string | undefined = undefined;

        switch (item.objectType) {
            case eLynxObjectTypeEnum.Well:
                url = getWellRoute(UIViewEnum.Well_Details_AlarmSetpoints,
                    groupID,
                    item.id);
                break;

            case eLynxObjectTypeEnum.Device:
                url = getDeviceRoute(UIViewEnum.Device_Details_AlarmSetpoints,
                    groupID,
                    undefined,
                    item.id);
                break;

            case eLynxObjectTypeEnum.Tank:
                url = getTankRoute(UIViewEnum.Tank_Details_AlarmSetpoints,
                    groupID,
                    undefined,
                    undefined,
                    item.id);
                break;

            case eLynxObjectTypeEnum.TankBattery:

                url = getTankBatteryRoute(UIViewEnum.TankBattery_Details_AlarmSetpoints,
                    groupID,
                    item.id);
                break;
        }

        if (url) {
            navigateTo(url);
        }
    }

    function onEdit(item: IChartCountModel) {

        switch (item.elynxObject.objectType) {
            case eLynxObjectTypeEnum.SetPoint:
                onEditSetpoint(item.elynxObject.id);
                break;

            case eLynxObjectTypeEnum.Recipient:
                const countItem = data?.objectCountItems.find(o => o.eLynxObject.id === item.elynxObject.id && o.eLynxObject.objectType === item.elynxObject.objectType);
                if (countItem && countItem.user)
                    onEditUser(countItem.user.id);
                break;

            default:
                navigateToObject(item.elynxObject);
                break;
        }
    }

    function onSort(expression: string) {
        if (state.sortExpression === expression) {
            setSortAscending(!state.sortAscending);
        } else {
            setSortExpression(expression);
            setSortAscending(false);
        }
    }

    function onReset() {
        //alert('reset sorting')
        resetSortState(true);
        //setSelectedIndex(undefined);
        setTarget(undefined);
        setSortExpression('Text');
        setSortAscending(false);

        onTargetSelected && onTargetSelected(undefined);

    }

    useEffect(() => {



        if (data) {
            const models = NotificationsDetailChartUtils.getChartData(data,
                showText,
                showEmail,
                showVoice,
                state.sortExpression,
                state.sortAscending
            );
            setChartData(models);
        }
    }, [
        data,
        setChartData,
        showText,
        showEmail,
        showVoice,
        state.sortExpression,
        state.sortAscending
    ]);

    useEffect(() => {
        setTarget(targetObject);
    }, [
        targetObject,
        setTarget
    ]);


    return (
        <>
            <DashboardContainer 
                dashboardContainerType={DashboardContainerType.Graph}
                title={title}
                loading={status === 'loading'}
                actions={notificationDetailActionItems}
                onExecuteAction={() => onReset()}
                >

                {!data ?
                    <NoData text={t('alarmDashboard.noData')} />
                    :
                    <>

                    <Stack
                        direction='row'
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            justifyContent: 'flex-end',
                            marginBottom: 1,
                            minHeight: '1rem'
                        }}>
                            <AlarmNotificationChip
                                size='medium'
                                text="Text" 
                                color={chartColors[0]} 
                                showSort={state.sortExpression === 'Text'}
                                sortAscending={state.sortAscending}
                                onClick={() => onSort('Text')} 
                            />

                            <AlarmNotificationChip 
                                size='medium'
                                text="Email" 
                                color={chartColors[1]} 
                                showSort={state.sortExpression === 'Email'}
                                sortAscending={state.sortAscending}
                                onClick={() => onSort('Email')} />

                            {

                            allowVoiceAlarm && <AlarmNotificationChip 
                                size='medium'
                                text="Voice" 
                                color={chartColors[2]} 
                                showSort={state.sortExpression === 'Voice'}
                                sortAscending={state.sortAscending}
                                onClick={() => onSort('Voice')} />
                            }
                    </Stack>                

                    <List dense={true}
                        sx={{
                            paddingTop: 0,
                            overflowX: "hidden",
                            overflowY: isPhone ? undefined : "auto",
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 auto'
                        }}>
                            {state.chartData?.map((o, ndx) => 

                                <NotificationsDetailListItem key={ndx}
                                    notificationsType={notificationsType}
                                    item={o}
                                    selected={selectedIndex === ndx}
                                    onEdit={onEdit}
                                    onSelected={(model) => {
                                        setTarget(model.elynxObject);
                                        onTargetSelected && onTargetSelected(model.elynxObject);
                                    }}
                                />

                                // <ListItem key={ndx} 
                                //     disablePadding 
                                //     disableGutters
                                //     secondaryAction={<ActionMenu
                                //         actionItems={[{
                                //             label: t(notificationsType === AlarmNotificationsType.Setpoints || 
                                //                     notificationsType === AlarmNotificationsType.Objects ? 
                                //                     "actions.editAlarmSetpoint" : 
                                //                     "actions.editUser"
                                //                     ),
                                //             action: NotificationDetailActionEnum.Edit,
                                //             onClick: () => onEdit(o), 
                                //             order: 0
                                //         }]}
                                //     />
                                //     }>
                                //         <ListItemButton 
                                //             sx={{
                                //                 paddingTop: 0,
                                //                 paddingBottom: 0,
                                //                 backgroundColor: selectedIndex === ndx ? `${theme.palette.grey[400]}!important` : undefined
                                //             }}
                                //             selected={selectedIndex === ndx}
                                //             //onClick={() => setSelectedIndex(ndx)}>
                                //             onClick={() => {
                                //                 setTarget(o.elynxObject);
                                //                 onTargetSelected && onTargetSelected(o.elynxObject);
                                //             }}>

                                //             <ListItemText primary={o.elynxObject.displayText} 
                                //                 sx={{
                                //                     display: 'flex',
                                //                     flex: '1 1 40%'
                                //                 }}
                                //                 primaryTypographyProps={{
                                //                     fontSize: '.8rem',
                                //                     whiteSpace: 'normal',
                                //             }} />


                                //             <Box sx={{
                                //                 position: 'relative',
                                //                 padding: 0,
                                //                 width: '60%',
                                //                 borderLeft: '1px solid silver!important',
                                //                 maxWidth: 275 }}>

                                //                 <HighchartsReact
                                //                     highcharts={Highcharts}
                                //                     options={o.chartOptions}
                                //                     //callback={chartCallback}
                                //                     containerProps={{
                                //                         style: { height: "100%" }
                                //                     }}
                                //                 />
                                //             </Box>

                                //         </ListItemButton>                                
                                //     </ListItem>
                            )}
                    </List>
                </>
                }

            </DashboardContainer>
            <AlarmSetpointEditorDialog {...bindAlarmSetpointEditorDialog(setpointEditorDialog)} />
            <UserEditorDialog {...bindUserEditorDialog(userEditorDialog)} />
        </>
    );
}
